/**
 * RainRipples — animated raindrop rings on up-facing surfaces (shader wave
 * stage 4, 2026-09-09), after Sébastien Lagarde's "Water drop 2b – Dynamic
 * rain and its effects" and Unity's Shader Graph weather sample: the plane is
 * cut into cells, every cell spawns one drop at a random phase, and the drop
 * is a ring whose radius grows with time while its amplitude decays — the
 * ring's height derivative is the normal perturbation. Four cell layers at
 * different scales and phases overlap so no grid shows.
 *
 * Purely procedural (no texture): `rippleHeightCpu` is the ring profile the
 * tests pin, the shader evaluates the same function's derivative.
 */

import type * as THREE from 'three/webgpu';
import {
  Fn, uniform, float, vec2, vec3, vec4, floor, fract, time, sin, exp, saturate, normalView, normalWorld,
  positionWorld, cameraViewMatrix, smoothstep, mix, If,
} from 'three/tsl';
import { resolveSurfaceGradient, offsetAsSurfaceGradient } from './SurfaceGradient.js';

export interface RainRipplesConfig {
  /** 0..1 — the rain (0 = no ripples). Default 0. */
  intensity?: number;
  /** Cell size in metres (default 0.35). */
  cellSize?: number;
  /** Drops per second per cell (default 1.4). */
  rate?: number;
  /** Normal perturbation strength (default 0.35). */
  strength?: number;
  /**
   * Optional per-fragment mask (a float node, 1 = rain lands here, 0 = it
   * does not) multiplied into the strength — Atlas passes its roof map's
   * exposure so no ring forms under a porch (render wave 1, 2026-09-17).
   */
  maskNode?: unknown;
}

export interface RainRipplesHandle {
  intensity: { value: number };
  strength: { value: number };
}

/** One ring: height at radius r, age t (s). Rings grow at `speed`, decay over ~0.6 s. */
export function rippleHeightCpu(r: number, t: number, speed = 0.55): number {
  if (t < 0 || t > 1) return 0;
  const radius = t * speed;
  const d = r - radius;
  const env = Math.exp(-t * 4) * (1 - t);
  return Math.sin(-d * 60) * Math.exp(-d * d * 400) * env;
}

/** Whether a surface takes rain: world-up facing (n.y ≥ 0.6 ramps in). */
export function rainReceiveCpu(normalY: number): number {
  const t = (normalY - 0.4) / 0.4;
  const s = t < 0 ? 0 : t > 1 ? 1 : t;
  return s * s * (3 - 2 * s);
}

/**
 * Add rain ripples to a material's normal (view space, composed over its
 * existing normalNode or the geometry normal). Only up-facing fragments.
 */
export function applyRainRipples(
  material: THREE.Material & { normalNode?: unknown },
  config?: RainRipplesConfig,
): RainRipplesHandle {
  const uIntensity = uniform(config?.intensity ?? 0);
  const uStrength = uniform(config?.strength ?? 0.35);
  const cell = config?.cellSize ?? 0.35;
  const rate = config?.rate ?? 1.4;
  const prev = material.normalNode;

  material.normalNode = Fn(() => {
    const base = vec3((prev ?? normalView) as never);
    const p = positionWorld.xz;
    // Sum of four cell layers; each cell's drop has its own phase.
    const gx = float(0).toVar(), gz = float(0).toVar();
    const layers: Array<[number, number]> = [[1, 0], [1.37, 0.31], [0.71, 0.63], [1.91, 0.87]];
    // WHY gated: `k` below carries the intensity, so a dry day (Atlas's ground, every tier, most of the time) evaluated
    // four ring layers (8 hashes, 8 exp, 4 sin, a sqrt each) per ground pixel to multiply them by 0. A uniform branch;
    // the result is identical.
    If(uIntensity.greaterThan(0), () => {
    for (const [scale, phaseOff] of layers) {
      const cp = p.div(cell * scale);
      const id = floor(cp);
      const fr = fract(cp).sub(0.5);
      const h = fract(id.x.mul(127.1).add(id.y.mul(311.7)).sin().mul(43758.5453));
      const h2 = fract(id.x.mul(269.5).add(id.y.mul(183.3)).sin().mul(43758.5453));
      const centre = vec2(h.sub(0.5).mul(0.6), h2.sub(0.5).mul(0.6));
      const rel = fr.sub(centre);
      const r = rel.length();
      const t = fract(time.mul(rate).add(h.mul(7)).add(phaseOff));
      const radius = t.mul(0.55);
      const d = r.sub(radius);
      const env = exp(t.mul(-4)).mul(float(1).sub(t));
      // dh/dr of sin(-60d)·exp(-400d²)·env — the ring's slope
      const g = sin(d.mul(-60)).mul(exp(d.mul(d).mul(-400))).mul(env);
      const slope = g.mul(float(60)).mul(saturate(float(1).sub(r.mul(2.2))));
      const dir = rel.div(r.max(1e-4));
      gx.addAssign(dir.x.mul(slope));
      gz.addAssign(dir.y.mul(slope));
    }
    });
    const upFacing = smoothstep(float(0.4), float(0.8), normalWorld.y);
    let k = uStrength.mul(uIntensity).mul(upFacing).mul(0.02); // intensity applied ONCE (a second mix by it squared the response)
    if (config?.maskNode !== undefined && config.maskNode !== null) k = k.mul(float(config.maskNode as never)); // the roof map's exposure (render wave 1)
    const worldOff = vec3(gx.mul(k), float(0), gz.mul(k));
    const viewOff = cameraViewMatrix.mul(vec4(worldOff, 0)).xyz;
    // THE NORMAL CONTRACT (material models, 2026-09-30): the ring's world-horizontal slope ADDED to the prior normal is
    // the unprojected offset form — resolve(n, −v) = normalize(n + v), the same arithmetic as before (SurfaceGradient.ts)
    return mix(base, resolveSurfaceGradient(base, offsetAsSurfaceGradient(viewOff)), upFacing);
  })();
  material.needsUpdate = true;

  return {
    intensity: uIntensity as unknown as { value: number },
    strength: uStrength as unknown as { value: number },
  };
}
